import {
  ApplicationInitStatus,
  ApplicationRef,
  inject,
  Injector,
  Provider,
  runInInjectionContext,
  signal,
  Type,
} from '@angular/core';

import moment from 'moment-timezone';
import { lastValueFrom, Subject } from 'rxjs';

import { APP_CONFIG } from '@core/common-legacy';
import { patchMoment } from '@core/i18n-legacy';
import { injectDevFlagPromise } from '@core/tellent-dev-flags-logic';
import {
  TellentMainNavigationStateService,
  TellentNavigationDataFacade,
  TellentNavigationRoutingService,
} from '@core/tellent-logic';
import {
  AppKind,
  injectTellentAppKind,
  ORG_GUID,
  TELLENT_ADOPTED_STYLESHEETS,
  TELLENT_CLIENT_CONFIG,
  TELLENT_INITIAL_CURRENT_USER,
  TellentAdminAppConfig,
  TellentClientBootstrapData,
  TellentClientBootstrapDataV2,
  TellentClientConfig,
  TellentClientInitData,
  TellentCurrentUser,
  TellentDevFlag,
} from '@core/tellent-types';
import { injectTellentAppUrlBuilder } from '@core/tellent-util';
import { defineCustomElements } from '@tellent/tellent-util';
import { authorize, handleAuthError, TellentClientAuthError } from '@tellent-client/auth-logic';

import { bootstrapInjectorProviders, resolveAppProviders } from './app.config';
import { environments } from './app.environments';
import { AppRevisionToken } from './app.revision';
import { TellentClientBootstrap } from './bootstrap';
import { redirectToOneDomainIfLegacyDomainVisited } from './redirect-to-one-domain-if-legacy-domain-visited';
import { TellentClientGlobalNotificationsService } from './services/tellent-client-global-notifications.service';
import { resolveErrorHandlerProviders } from './tellent-client-error-handling';
import { TellentAddMemberDialogWrapComponent } from './wrappers/add-member-dialog/add-member-dialog.component';
import { TellentAppShellWrapComponent } from './wrappers/app-shell/app-shell.component';
import { TellentCompanyVerificationDialogWrapComponent } from './wrappers/company-verification-dialog/company-verification-dialog.component';
import { TellentCurrentUserProfileDialogWrapComponent } from './wrappers/current-user-profile-dialog/current-user-profile-dialog-wrap.component';
import { EditMemberDialogWrapComponent } from './wrappers/edit-member-dialog/edit-member-dialog.component';
import { TellentFeatureDiscoveryDialogWrapComponent } from './wrappers/feature-discovery-dialog/feature-discovery-dialog.component';
import { TellentProfileDropdownWrapComponent } from './wrappers/profile-dropdown/profile-dropdown-wrap.component';
import { TellentReportsShellComponent } from './wrappers/reports-shell/reports-shell.component';

import 'moment/locale/de';
import 'moment/locale/nl';
import 'moment/locale/fr';
import 'moment/locale/fi';
import 'moment/locale/sv';

patchMoment(moment);
moment().locale('en');

const TellentClientComponents: Array<Type<any>> = [
  EditMemberDialogWrapComponent,
  TellentAppShellWrapComponent,
  TellentAddMemberDialogWrapComponent,
  TellentCompanyVerificationDialogWrapComponent,
  TellentCurrentUserProfileDialogWrapComponent,
  TellentProfileDropdownWrapComponent,
  TellentFeatureDiscoveryDialogWrapComponent,
  TellentReportsShellComponent,
];

export class TellentClient {
  #ready$ = new Subject<ApplicationRef>();

  #injector: Injector;
  #config: TellentClientConfig;
  #bootstrapProviders: Provider[];
  #dynamicConfig = {
    customStylesheets: signal<CSSStyleSheet[]>([]),
  };

  public readonly ready = lastValueFrom(this.#ready$);

  constructor(config: TellentClientConfig) {
    this.#config = this.validateConfig(config);
    this.#bootstrapProviders = bootstrapInjectorProviders(this.#config);
    this.#injector = Injector.create({
      name: 'Tellent client bootstrap injector',
      providers: [this.#bootstrapProviders],
    });
  }

  public async initWithSelfAuth(
    appKind: `${AppKind}`,
  ): Promise<Partial<TellentClientBootstrapDataV2> | null | void> {
    return runInInjectionContext(this.#injector, async () => {
      const config = inject<TellentAdminAppConfig>(APP_CONFIG);

      try {
        const authData = await authorize(config, appKind, <any>this.#config.useOrgFallback ?? true);
        const initData = await this.init(authData, authData.user);
        return {
          ...authData,
          ...initData,
        };
      } catch (error) {
        if (error instanceof TellentClientAuthError) {
          const result = {
            ...(handleAuthError(error, config) || {}),
            appKind,
          };

          if (result.continueOnError) {
            const initData = await this.init(result, result.user);

            return {
              ...result,
              ...initData,
            };
          }
        }

        throw error;
      }
    });
  }

  public async init(
    credentials: Partial<TellentClientInitData>,
    user?: TellentCurrentUser,
  ): Promise<TellentClientBootstrapData> {
    const { appRef } = await this.#injector.get(TellentClientBootstrap).createApplication(
      [
        ...this.#bootstrapProviders,
        ...resolveErrorHandlerProviders(this.#config, credentials.appGuid),
        ...resolveAppProviders(this.#config),
        { provide: TELLENT_CLIENT_CONFIG, useValue: Object.freeze({ ...this.#config }) },
        {
          provide: TELLENT_ADOPTED_STYLESHEETS,
          useValue: this.#dynamicConfig.customStylesheets.asReadonly(),
        },
      ],
      this.#config,
      credentials,
      user,
    );

    const oneSettings = await runInInjectionContext(appRef.injector, () =>
      injectDevFlagPromise(TellentDevFlag.OneSettings),
    );

    const oneDomain = await runInInjectionContext(appRef.injector, () =>
      injectDevFlagPromise(TellentDevFlag.OneDomain),
    );

    runInInjectionContext(appRef.injector, () =>
      this.#config.env.includes('local')
        ? null
        : redirectToOneDomainIfLegacyDomainVisited(
            oneDomain,
            inject<TellentAdminAppConfig>(APP_CONFIG),
          ),
    );

    appRef.injector.get(TellentClientGlobalNotificationsService);

    defineCustomElements(TellentClientComponents, appRef.injector);

    await appRef.injector.get(ApplicationInitStatus).donePromise;

    this.#ready$.next(appRef);
    this.#ready$.complete();

    const orgGuid = appRef.injector.get(ORG_GUID);
    const member = appRef.injector
      .get(TELLENT_INITIAL_CURRENT_USER)
      .members.find(member => member.orgGuid === orgGuid)!;

    const version = appRef.injector.get(AppRevisionToken);

    // eslint-disable-next-line no-console
    console.log(
      `[TellentClient] ${version}\norgGuid: "${orgGuid}"\nappGuid: "${credentials.appGuid}"\nmemberGuid: "${member?.guid}"`,
    );

    return {
      member,
      features: { oneSettings, oneDomain },
    };
  }

  public getTellentUrl(appKind: `${AppKind}`, page: string, params: object = {}, orgGuid?: string) {
    return this.ready.then(appRef =>
      runInInjectionContext(appRef.injector, () => {
        const urlBuilder = injectTellentAppUrlBuilder();
        const _orgGuid = orgGuid ?? inject(ORG_GUID);

        return urlBuilder(
          appKind,
          {
            id: <any>page,
          },
          _orgGuid!,
        );
      }),
    );
  }

  public reload() {
    this.ready.then(appRef =>
      runInInjectionContext(appRef.injector, () => {
        const urlBuilder = injectTellentAppUrlBuilder();
        const appKind = injectTellentAppKind();
        const orgGuid = inject(ORG_GUID);

        window.open(urlBuilder(appKind, undefined, orgGuid!), '_self');
      }),
    );
  }

  public get navigation() {
    return this.ready.then(appRef => {
      const routingSvc = appRef.injector.get(TellentNavigationRoutingService);
      const mainNavSvc = appRef.injector.get(TellentMainNavigationStateService);
      const dataFacade = appRef.injector.get(TellentNavigationDataFacade);

      return {
        refresh: () => dataFacade.refreshMainNav(),
        registerLinkHandler: (handler: (url: string) => void) =>
          routingSvc.registerNavHandler(handler),
        location: routingSvc.location,
        recognizedRoutes: routingSvc.recognizedRoutes,
        minimized: mainNavSvc.minimized,
        forceMaximized: mainNavSvc.forceMaximized,
      };
    });
  }

  public setSharedStylesheets(stylesheets: CSSStyleSheet[]) {
    this.#dynamicConfig.customStylesheets.set(stylesheets);
  }

  private validateConfig(config: TellentClientConfig): TellentClientConfig {
    const envs = Object.keys(environments);

    if (!config.env || !envs.includes(config.env)) {
      throw new Error(
        `Missing required property:\n"config.env": ${envs.map(e => `"${e}"`).join(' | ')}`,
      );
    }

    return config;
  }
}
